import { useState, useMemo, useEffect, useCallback, useContext, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import variables from 'styles/vendor/bootstrap/variables'
import { useInView } from 'react-intersection-observer'
import { FormattedMessage, useIntl } from 'react-intl'

import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { FlightsClassTypes, fareClassToUrlMap, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import {
  MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
  MALDIVES_LANDING_BANNER_IMAGE_ID,
  MALDIVES_DREAM_TILE_TITLE_MESSAGES,
  MALDIVES_DREAM_TILES,
} from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TextButton from 'LuxKit/Button/TextButton'
import Image from 'components/Common/Image'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineSmileIcon from 'LuxKit/Icons/line/LineSmileIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextLink from 'LuxKit/TextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFilters from './MaldivesDirectSearchFilters'
import MaldivesDirectSearchCalendarModal from './MaldivesDirectSearchCalendarModal'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelPriceToggle from 'components/Hotels/HotelPriceToggle'
import { parseAsBoolean, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { buildSuggestedDatesParamsKey, encodeChildrenOccupancy, parseOccupancyFromParams } from 'lib/search/searchUtils'
import { queryNullChildren } from 'constants/search'
import useFlightAndHotelBundle from 'hooks/FlightAndHotelBundle/useFlightAndHotelBundle'
import Places from 'constants/places'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { OFFER_SET_SEARCH_RESULT_METADATA } from 'actions/actionConstants'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { useElementWidth } from 'hooks/useElementSize'
import { useFlightItemData } from './hooks/useFlightItemData'
import { filterDateRangesByNights } from './hooks/utils'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import MaldivesCallPrompt from './MaldivesCallPrompt'
import MaldivesOnePointPromoBanner from 'components/MaldivesDirect/OnePointPromo/MaldivesOnePointPromoBanner'
import useMaldivesOnePointPromo from 'components/MaldivesDirect/OnePointPromo/useMaldivesOnePointPromo'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { EmptyArray } from 'lib/array/arrayUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { MaldivesDirectBundleProvider } from './MaldivesDirectBundleContext'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import {
  ALL_CHARTER_FARE_CLASSES,
  getCharterFareClassPolicy,
  getDefaultFareClassFromAllowed,
  getUnionAllowedFareClasses,
} from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { hasFeederFlightPriced as hasFeederFlightPricedHelper } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import zIndex from 'styles/tools/z-index'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { isLEStaff } from 'selectors/accountSelectors'
import DreamBlock from 'components/Pages/LandingPage/Hub/AdditionalGuides/DreamBlock'
import OfferSortControl from 'components/Common/OfferSortControl'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { replace } from 'lib/routing/appRouting'
import { pushWithRegion } from 'actions/NavigationActions'
import { useMaldivesDirectHotelStreamSearch } from './hooks/useMaldivesDirectHotelStreamSearch'
import AnywhereFilterTabs from 'components/Pages/HotelSearchPage/AnywhereFilterTabs'
import FriendsAndFamilyClaimModal from 'components/FriendsAndFamilyDiscount/FriendsAndFamilyClaimModal'
import { FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM } from 'constants/friendsAndFamilyDiscount'
import MaldivesCharterRewardSeatsInfoModal from 'components/Flights/CharterBundle/MaldivesCharterRewardSeatsInfoModal'
import useHashSignal from 'hooks/useHashSignal'
import { buildMaldivesDirectRoomsForPassengerCount, getMaldivesBundleCheckOutDate, getMaldivesDirectPassengerCountForHolidayTypes } from './MaldivesDirectUtils'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

type BundleType = '5-nights' | '12-nights'
type OfferMode = 'hotels' | 'tourBundles'

const OPEN_DATE_SELECTION_QUERY_PARAM = 'openDateSelection'
const ENABLED_QUERY_PARAM_VALUES = new Set(['true', '1'])
const HUNDRED_DOLLAR_SALE_FULL_TERMS_URL = 'https://luxuryescapes.com/au/promotions'

const MELBOURNE_AIRPORT: App.Airport = {
  code: 'MEL',
  name: 'Tullamarine',
  latitude: -37.8136,
  longitude: 144.9631,
  countryCode: 'AU',
}

/** The campaign section's mobile side inset, per the landing page design. */
const MOBILE_CAMPAIGN_INSET = 20

/**
 * `HeroBannerSection` is full bleed on mobile, so the contact prompt carries the design's
 * own 20px inset there; from tablet up the section's gutter padding takes over.
 */
const ContactPromptRow = styled(Group)`
  padding-inline: ${rem(MOBILE_CAMPAIGN_INSET)};

  ${mediaQueryUp.tablet} {
    padding-inline: 0;
  }
`

/**
 * Gap between the contact prompt and the campaign banner: 20px on mobile, 30px from
 * tablet up. The banner also matches the design's 20px mobile inset rather than the
 * narrower default page gutter, so it lines up with the prompt above it.
 */
const PromoBannerContainer = styled(LayoutContainer)`
  margin-block-start: ${rem(20)};
  padding-inline: ${rem(MOBILE_CAMPAIGN_INSET)};

  ${mediaQueryUp.tablet} {
    margin-block-start: ${rem(30)};
    padding-inline: ${rem(variables.gutterWidth / 2)};
  }
`

const HeroBannerSection = styled(LayoutContainer)`
  display: grid;
  gap: ${rem(40)};

  /* Full bleed on mobile. */
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
    padding-block-start: ${rem(30)};
    padding-inline: ${rem(variables.gutterWidth / 2)};
  }
`

const HeroBanner = styled.div`
  display: grid;
  grid-template-areas: 'image' 'panel';
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    border-radius: ${props => props.theme.borderRadius.XL};
    /* Matches the banner photo's aspect in this column, so it is never cropped. */
    min-height: ${rem(341)};
    grid-template-areas: 'panel image';
    grid-template-columns: minmax(0, 630fr) minmax(0, 510fr);
  }
`

const HeroPanel = styled.div`
  grid-area: panel;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  padding-block: ${rem(28)};
  padding-inline: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(40)};
    padding-inline: ${rem(40)};
  }
`

const HeroCtaButtons = styled(Group)`
  > * {
    flex: 1 1 0;
  }

  ${mediaQueryUp.tablet} {
    > * {
      flex: 0 0 auto;
    }
  }
`

const HeroBannerMedia = styled.div`
  grid-area: image;
  position: relative;
  overflow: hidden;
  aspect-ratio: 375 / 180;

  ${mediaQueryUp.tablet} {
    aspect-ratio: auto;
  }
`

const HeroBannerImage = styled(Image)`
  position: absolute;
  inset: 0;
`

/* Must stay square — a non-square box turns the 50% corners elliptical. */
const HeroBannerArc = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline-start: 9.83%;
  width: 79.3%;
  aspect-ratio: 1;
  transform: translateY(-8.08%);
  pointer-events: none;
  border: ${rem(4)} solid ${props => props.theme.palette.neutral.surface.staticWhite};
  border-start-start-radius: 50%;
  border-start-end-radius: 50%;
  border-end-end-radius: 3.83%;
  border-end-start-radius: 50%;
`

const ModeToggleBottomSpacer = styled.div`
  height: ${rem(16)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  body.${REBRAND_PALETTE_CLASS} & {
    background-color: var(--palette-highlight-primary-dark-background);
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(28)};
  }
`

function MaldivesSupportPhoneNumber({ headingVariant = 'heading5' }: { headingVariant?: 'heading5' | 'heading3' }) {
  return (
    <CSSBreakpoint min="tablet">
      <TextLink
        href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
        startIcon={<LinePhoneIcon />}
        size="large"
        underline={false}
        block
      >
        <Heading variant={headingVariant} as="span">
          {MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY}
        </Heading>
      </TextLink>
    </CSSBreakpoint>
  )
}

// Offers Section Styles
const OffersSection = styled.section`
  scroll-margin-block-start: ${props => props.theme.layout.topScrollOffset};
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const OffersContainer = styled(LayoutContainer)`
  max-width: ${rem(1200)};
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(20)};
  }
`

const MobileFilterTabsPadding = styled.div`
  /* The inner CardCarousel uses negative horizontal margins to bleed past its parent's gutter,
   * which only works when both sides are padded by the carousel gutter width. With left-only
   * padding the right-side negative margin overshoots the viewport and causes mobile horizontal
   * wobble (clipped by body's overflow-x: hidden, but the layout still shifts). Use
   * padding-inline so both sides match. */
  ${mediaQueryDown.tablet} {
    padding-inline: ${rem(20)};
  }
`

const OffersHeader = styled(Group)`
  margin-bottom: ${rem(24)};

  ${mediaQueryDown.tablet} {
    padding-inline: ${rem(20)};
  }
`

const OfferModeToggleWrapper = styled.div`
  position: relative;
  margin: 0 0 ${rem(16)};
  z-index: 1;

  ${mediaQueryUp.tablet} {
    --maldives-direct-sticky-controls-inline-translate: -50%;

    position: absolute;
    top: 0;
    margin: 0;
    padding: 0 ${rem(12)};
    display: flex;
    justify-content: center;
    inset-inline-start: 50%;
    transform: translateY(-50%) translateX(var(--maldives-direct-sticky-controls-inline-translate));
    transition: transform 0.3s, top 0.3s;
    z-index: 1;

    [dir='rtl'] & {
      --maldives-direct-sticky-controls-inline-translate: 50%;
    }

    &.full-size {
      position: fixed;
      top: ${props => props.theme.layout.headerFloatingOffset};
      transform: translateY(0) translateX(var(--maldives-direct-sticky-controls-inline-translate));
      z-index: ${zIndex.stickyHeader};
    }
  }
`

const MobileOfferModeToggleStickyWrapper = styled(CSSBreakpoint)`
  position: sticky;
  top: ${props => props.theme.layout.headerFloatingOffset};
  /* No negative horizontal margins — the page root has no horizontal padding, so any negative
   * margin here pushes the toggle past the viewport edge and causes mobile horizontal-pan
   * wobble (clipped visually by body's overflow-x: hidden, but still affects layout). */
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: ${zIndex.stickyHeader};
`

const OfferModeToggleInner = styled.div`
  position: relative;
`

const MobileModeToggleButtons = styled(Group)`
  width: 100%;
`

const MobileModeTabButton = styled(TabButton)`
  flex: 1;
  min-width: 0;
`

const OfferModeTogglePane = styled(Pane)`
  position: absolute;
  top: 0;
  inset-inline: 0;
  bottom: 0;

  ${mediaQueryUp.tablet} {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: 2px;
    transform: scaleX(1);
    transition: transform 0.3s;
    transform-origin: center top;

    &.full-size {
      transform: scaleX(var(--menu-scale, 1));
    }
  }
`

const ContactBannerContainer = styled(LayoutContainer)`
  margin-top: ${rem(30)};
`

const MALDIVES_DIRECT_OFFERS_SECTION_ID = 'maldives-direct-offers-heading'
const MALDIVES_DIRECT_SEARCH_FILTERS_HEADING_ID = 'maldives-direct-search-filters-heading'
const isMelbourneAirport = (airport?: App.Airport) => ['AVV', 'MBW', 'MEB', 'MELALL'].includes(airport?.code ?? 'MEL')

interface ModeToggleTabsProps {
  variant: 'desktop' | 'mobile'
  isTourBundleMode: boolean
  onHotelsModeClick: () => void
  onTourBundlesModeClick: () => void
  showFullSize?: boolean
  modeToggleScale?: number
  modeToggleRef?: React.RefObject<HTMLDivElement | null>
}

function ModeToggleTabs({
  variant,
  isTourBundleMode,
  onHotelsModeClick,
  onTourBundlesModeClick,
  showFullSize = false,
  modeToggleScale = 1,
  modeToggleRef,
}: ModeToggleTabsProps) {
  const intl = useIntl()
  const isMobile = variant === 'mobile'
  const hotelsMobileLabel = intl.formatMessage({
    id: 'qOtWA4_OicqojNu_wYq0',
    defaultMessage: 'Hotel + flight',
    description: 'Mode toggle label for hotel + flight on mobile in the maldives direct search page section',
  })
  const hotelsDesktopLabel = intl.formatMessage({
    id: 'fUfszU5WERwX82Ju-mM8',
    defaultMessage: 'Hotel + flight bundles',
    description: 'Mode toggle label for hotel + flight bundles on desktop',
  })
  const toursMobileLabel = intl.formatMessage({
    id: 'E-Mjs5oUdSQ9m6QW70la',
    defaultMessage: 'Tour + flight',
    description: 'Mode toggle label for tour + flight on mobile in the maldives direct search page section',
  })
  const toursDesktopLabel = intl.formatMessage({
    id: 's3G4_TeOzBMstPBo07c5',
    defaultMessage: 'Tour + flight bundles',
    description: 'Mode toggle label for tour + flight bundles on desktop',
  })
  const hotelsLabel = isMobile ? hotelsMobileLabel : hotelsDesktopLabel
  const toursLabel = isMobile ? toursMobileLabel : toursDesktopLabel

  if (isMobile) {
    return (
      <OfferModeToggleInner>
        <MobileModeToggleButtons direction="horizontal" gap={0}>
          <MobileModeTabButton
            size="medium"
            isActive={!isTourBundleMode}
            startIcon={<LineBedIcon />}
            onClick={onHotelsModeClick}
          >
            {hotelsLabel}
          </MobileModeTabButton>
          <MobileModeTabButton
            size="medium"
            isActive={isTourBundleMode}
            startIcon={<LineCompassIcon />}
            onClick={onTourBundlesModeClick}
          >
            {toursLabel}
          </MobileModeTabButton>
        </MobileModeToggleButtons>
      </OfferModeToggleInner>
    )
  }

  return (
    <OfferModeToggleInner ref={modeToggleRef}>
      <OfferModeTogglePane
        className={showFullSize ? 'full-size' : undefined}
        style={{ '--menu-scale': modeToggleScale } as React.CSSProperties}
      />
      <TabButtonsGroup fit="grow" tabletFit="auto">
        <TabButton
          size="medium"
          isActive={!isTourBundleMode}
          startIcon={<LineBedIcon />}
          onClick={onHotelsModeClick}
        >
          {hotelsLabel}
        </TabButton>
        <TabButton
          size="medium"
          isActive={isTourBundleMode}
          startIcon={<LineCompassIcon />}
          onClick={onTourBundlesModeClick}
        >
          {toursLabel}
        </TabButton>
      </TabButtonsGroup>
    </OfferModeToggleInner>
  )
}

function MaldivesDirectSearchPage() {
  const intl = useIntl()
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })
  const formatAudCompactCurrency = useCurrencyFormatter({ currency: 'AUD', notation: 'compact' })
  const hundredDollarSaleFareAmount = formatAudCurrency(100, 'roundedDollar')
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const dreamTileReadMoreLabel = intl.formatMessage({
    id: 'bZfEQWN0g_O1NypsL6K3',
    defaultMessage: 'Read more',
    description: 'Link text for inspiration article cards in the Maldives dream block on the Maldives Direct search page',
  })
  const dreamTiles: Array<App.HubTile> = MALDIVES_DREAM_TILES.map(tile => ({
    ...tile,
    title: intl.formatMessage(
      MALDIVES_DREAM_TILE_TITLE_MESSAGES[tile.image],
      tile.image === 'maldives-your-way-from-no-expense-spared-to-under-$5k' ? { budget: formatAudCompactCurrency(5000, 'roundedDollar') } : undefined,
    ),
    linkText: dreamTileReadMoreLabel,
  }))
  const isRegionSupported = useAppSelector(isMaldivesCharterRegionEnabled)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterFnfPage)
  const isReturnFlightFreeFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterReturnFlightFree)
  const isCharterLaunchOffer100FeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.charterLaunchOffer100)
  const isStaff = useAppSelector(isLEStaff)
  const isCharterLaunchOffer100Enabled =
    isCharterLaunchOffer100FeatureEnabled && !isReturnFlightFreeFeatureEnabled && !isAgentHubEnabled && !isStaff
  const canShowFamilyAndFriendsDiscountChip = isStaff && isFriendsAndFamilyPageEnabled
  const windowSearch = useAppSelector(state => state.router.location.search)
  const maldivesCharterEnabled = isRegionSupported && (config.MALDIVES_CHARTER_ENABLED || agentHubOrSubBrandConfig.isEnabled)
  const dispatch = useAppDispatch()
  const openModal = useContext(ModalContext)
  const hasOpenedFriendsAndFamilyClaimModalRef = useRef(false)
  const hasOpenedDateSelectionModalRef = useRef(false)

  // Detect when user has scrolled past the hero banner
  const { ref: heroDetectorRef, inView: heroInView } = useInView({
    threshold: 0,
    initialInView: true,
  })

  // State to track if user has manually closed the prompt
  const [isCallPromptClosed, setIsCallPromptClosed] = useState(false)
  const modeToggleRef = useRef<HTMLDivElement | null>(null)
  const modeToggleWidth = useElementWidth(modeToggleRef as React.MutableRefObject<HTMLElement | null>)
  const [viewportWidth, setViewportWidth] = useState(0)

  // Flight and Hotel bundle management
  const { updateFlightData, updateSelectedDates } = useFlightAndHotelBundle()

  // Occupancy query params - encoded as two separate query params (adults, children)
  // Default: 2 adults, no children (encoded as "2" for adults, "none" for children)
  const [adultsParam, setAdultsParam] = useQueryState('adults', parseAsString.withDefault('2'))
  const [childrenParam, setChildrenParam] = useQueryState('children', parseAsString.withDefault(queryNullChildren))

  // Parse the query params into rooms array, ensuring teenagers field is included for consistency
  const rooms = useMemo<Array<App.Occupants>>(
    () => parseOccupancyFromParams(adultsParam, childrenParam).map(room => ({
      ...room,
      teenagers: room.teenagers ?? 0,
    })),
    [adultsParam, childrenParam],
  )

  const onePointPromo = useMaldivesOnePointPromo(MALDIVES_DIRECT_OFFERS_SECTION_ID, rooms)

  // Helper function to update rooms (updates both query params)
  const setRooms = useCallback((newRooms: Array<App.Occupants>) => {
    const adultsString = newRooms.map(room => room.adults).join(',')
    const childrenString = newRooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(',')

    setAdultsParam(adultsString)
    setChildrenParam(childrenString)
  }, [setAdultsParam, setChildrenParam])

  // Departure airport - the user's retained airport (recently used, falling back to closest), or Melbourne
  const departureAirportOfUser = useAppSelector(getDefaultAirport)

  const isMelbourneDefaultAirport = isMelbourneAirport(departureAirportOfUser) || agentHubOrSubBrandConfig.isEnabled
  const [departureAirport, setDepartureAirport] = useState<App.Airport>(isMelbourneDefaultAirport ? MELBOURNE_AIRPORT : departureAirportOfUser ?? MELBOURNE_AIRPORT)
  const hasUserSelectedAirport = useRef(false)

  // The retained airport resolves after mount on full page loads (account/geo state hydrates
  // asynchronously), so without this sync the Melbourne fallback would stick for the whole visit
  useEffect(() => {
    if (hasUserSelectedAirport.current || agentHubOrSubBrandConfig.isEnabled || !departureAirportOfUser) {
      return
    }
    setDepartureAirport(isMelbourneAirport(departureAirportOfUser) ? MELBOURNE_AIRPORT : departureAirportOfUser)
  }, [departureAirportOfUser])

  // sync airport to redux when agent hub is enabled so offer pages inherit the correct airport
  useEffect(() => {
    if (agentHubOrSubBrandConfig.isEnabled && departureAirport.code === MELBOURNE_AIRPORT.code) {
      dispatch(setRecentlyUsedAirportCode(MELBOURNE_AIRPORT.code))
    }
  }, [departureAirport.code, dispatch])

  // Flight class - read raw URL value then map to internal type
  const { fareClassFromUrl: fareClass, setFareClassUrl: setFareClassRaw, cabinFilter: selectedCabin, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly, infiniteMemberOnly, setInfiniteMemberOnly } = useCharterFilterUrlState()
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const infiniteMemberOnlyForTier = isInfiniteTier && infiniteMemberOnly
  const setFareClassParam = useCallback(
    (fc: FareClassWithReward | null) => setFareClassRaw(fc ? fareClassToUrlMap[fc] : null),
    [setFareClassRaw],
  )
  // Stripped to base cabin for downstream search/analytics calls (only the modal/banner cares about
  // the reward variant, and they read `fareClass` directly).
  const fareClassForSearch: FlightsClassTypes = fareClass ? toBaseFareClass(fareClass) : FlightsClassTypes.ECONOMY

  // Bundle selection - '5-nights' or '12-nights'
  const [selectedBundle, setSelectedBundle] = useQueryState(
    'bundle',
    parseAsStringEnum<BundleType>(['5-nights', '12-nights'] as const).withDefault('5-nights'),
  )

  // LuxPlus toggle
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabledParam, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const luxPlusEnabled = luxPlusEnabledParam && isLuxPlusEnabled

  // Price display mode state (persisted in URL)
  const [priceMode, setPriceMode] = useQueryState(
    'priceMode',
    parseAsStringEnum<'total' | 'perPerson'>(['total', 'perPerson'] as const).withDefault('total'),
  )
  const [offerMode, setOfferMode] = useQueryState(
    'offerMode',
    parseAsStringEnum<OfferMode>(['hotels', 'tourBundles'] as const).withDefault('hotels'),
  )
  const isTourBundleModeRequested = offerMode === 'tourBundles'
  const modeToggleScale = useMemo(() => {
    if (!modeToggleWidth) {
      return 1
    }
    return viewportWidth / modeToggleWidth
  }, [modeToggleWidth, viewportWidth])
  const tourModeBundlesHeading = intl.formatMessage({
    id: 'w-ht91OUqcLgFTVsFBvg',
    defaultMessage: 'Exclusive tour + flight bundles',
    description: 'Heading shown above offers list when tour mode is active',
  })
  const familyAndFriendsDiscountLabel = intl.formatMessage({
    id: 'tgMHD5Q511ePE4ZaPcPM',
    defaultMessage: 'Family & Friends Discount',
    description: 'Desktop assist chip label shown on the Maldives direct page when a friends and family referral is active',
  })

  const handleFamilyAndFriendsDiscountClick = useCallback(() => {
    dispatch(pushWithRegion('/campaign/friends-and-family'))
  }, [dispatch])

  const [checkInDateParam, setCheckInDate] = useQueryState('checkIn', parseAsString)
  const [checkOutDateParam, setCheckOutDate] = useQueryState('checkOut', parseAsString)

  const checkInDate = checkInDateParam ?? undefined
  const checkOutDate = checkOutDateParam ?? undefined

  // Flight calendar modal state
  const [isFlightCalendarOpen, setIsFlightCalendarOpen] = useState(false)

  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Handle price mode change
  function handlePriceModeChange(newMode: 'total' | 'perPerson') {
    setPriceMode(newMode)
  }

  const handleHotelsModeClick = useCallback(() => {
    setOfferMode('hotels')
  }, [setOfferMode])

  const handleTourBundlesModeClick = useCallback(() => {
    setOfferMode('tourBundles')
  }, [setOfferMode])

  useEffect(() => {
    setViewportWidth(window.innerWidth)

    const handleViewportResize = () => {
      const nextViewportWidth = window.innerWidth
      setViewportWidth(previousViewportWidth => (
        previousViewportWidth === nextViewportWidth ? previousViewportWidth : nextViewportWidth
      ))
    }
    window.addEventListener('resize', handleViewportResize)

    return () => window.removeEventListener('resize', handleViewportResize)
  }, [])

  // Handle flight calendar modal
  function handleOpenFlightCalendar() {
    setIsFlightCalendarOpen(true)
  }

  function handleCloseFlightCalendar() {
    setIsFlightCalendarOpen(false)
  }

  function handleBundleChange(bundle: BundleType) {
    setSelectedBundle(bundle)
    const nights = bundle === '5-nights' ? 5 : 12
    setCheckOutDate(getMaldivesBundleCheckOutDate(checkInDate, nights))
  }

  function handleAirportChange(airport: App.Airport) {
    hasUserSelectedAirport.current = true
    setDepartureAirport(airport)
    dispatch(setRecentlyUsedAirportCode(airport.code))
  }

  function handleFlightClassChange(flightClass: FareClassWithReward | null) {
    setFareClassParam(flightClass)
  }

  // Check Economy Extra compatibility when rooms are applied (not on every intermediate change)
  const checkAndResetFareClassIfNeeded = useCallback((appliedRooms: Array<App.Occupants>) => {
    if (fareClass === FlightsClassTypes.LUXROW && !isSkyLoungeCompatible(appliedRooms)) {
      setFareClassParam(FlightsClassTypes.ECONOMY)
    }
  }, [fareClass, setFareClassParam])

  function handleLuxPlusToggle(enabled: boolean) {
    setLuxPlusEnabled(enabled)
  }

  function handleDateSelect(departDate: string, returnDate: string) {
    setCheckInDate(departDate)
    setCheckOutDate(returnDate)
  }

  // Handle flight calendar date selection (from modal)
  function handleFlightDateSelect(option: { departDate: string, returnDate: string, checkIn: string, checkOut: string, fareClass?: FareClassWithReward }) {
    // Use checkIn and checkOut (inner dates) for hotel queries
    setCheckInDate(option.checkIn)
    setCheckOutDate(option.checkOut)
    // Persist the selected cabin class to the URL
    if (option.fareClass !== undefined) {
      setFareClassParam(option.fareClass)
    }
    handleCloseFlightCalendar()
  }

  function handleMonthChange(month: string) {
    setSelectedMonth(month)
  }

  // Handle duration change from flight calendar
  function handleDurationChangeFromCalendar(nights: number) {
    const bundle = nights === 5 ? '5-nights' : '12-nights'
    setSelectedBundle(bundle)

    setCheckOutDate(getMaldivesBundleCheckOutDate(checkInDate, nights))
  }

  // Calculate nights based on selected bundle
  const nights = selectedBundle === '5-nights' ? 5 : 12

  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    rooms,
    fareClass: fareClassForSearch,
    feederOriginAirportCode: departureAirport.code,
  })

  const searchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const friendsAndFamilyReferralEmail = searchParams[FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM]
  const shouldOpenDateSelectionModal = ENABLED_QUERY_PARAM_VALUES.has(
    searchParams[OPEN_DATE_SELECTION_QUERY_PARAM]?.toLowerCase() ?? '',
  )

  useEffect(() => {
    if (!friendsAndFamilyReferralEmail || hasOpenedFriendsAndFamilyClaimModalRef.current) {
      return
    }

    hasOpenedFriendsAndFamilyClaimModalRef.current = true
    openModal(
      <FriendsAndFamilyClaimModal
        fnfEmailPrefix={friendsAndFamilyReferralEmail}
      />,
    )
  }, [friendsAndFamilyReferralEmail, openModal])

  const { clearHash: clearSocieteHash } = useHashSignal({
    hash: '#societe-rewards',
    onActivate: async() => {
      const viewDates = await openModal<boolean>(<MaldivesCharterRewardSeatsInfoModal />)
      clearSocieteHash()
      if (viewDates) {
        setIsFlightCalendarOpen(true)
      }
    },
  })

  const selectedHolidayTypesParam = searchParams.holidayTypes
  const previousSelectedHolidayTypesParamRef = useRef(selectedHolidayTypesParam)
  const selectedHolidayTypes = useMemo(() => {
    return selectedHolidayTypesParam?.split(',').filter(Boolean)
  }, [selectedHolidayTypesParam])
  const selectedHolidayTypePassengerCount = useMemo(
    () => getMaldivesDirectPassengerCountForHolidayTypes(selectedHolidayTypes),
    [selectedHolidayTypes],
  )
  useEffect(() => {
    if (previousSelectedHolidayTypesParamRef.current === selectedHolidayTypesParam) {
      return
    }

    previousSelectedHolidayTypesParamRef.current = selectedHolidayTypesParam

    if (selectedHolidayTypePassengerCount === undefined) {
      return
    }

    const nextRooms = buildMaldivesDirectRoomsForPassengerCount(selectedHolidayTypePassengerCount)
    setRooms(nextRooms)
    checkAndResetFareClassIfNeeded(nextRooms)
  }, [checkAndResetFareClassIfNeeded, selectedHolidayTypesParam, selectedHolidayTypePassengerCount, setRooms])
  const selectedAmenities = useMemo(() => {
    return searchParams.amenities?.split(',').filter(Boolean)
  }, [searchParams.amenities])

  const {
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  } = useOfferListSortOptions()
  const maldivesSortOptions = useMemo(() => [
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  ], [SORT_OPTION_RECOMMENDED, SORT_OPTION_PRICE_ASC, SORT_OPTION_VALUE, SORT_OPTION_PRICE_DESC, SORT_OPTION_CUSTOMER_RATING])
  const handleSortChange = useCallback(({ search }: { search: string }) => {
    dispatch(replace({ search }))
  }, [dispatch])

  const tourBundlePageSize = 25
  const tourBundleFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    flexibleMonths: '',
    flexibleNights: '',
    limit: tourBundlePageSize,
    isStream: false,
    showEnrichedResult: true,
  }), [])
  const tourBundleOfferList = useOfferList(tourBundleFilters, {
    minRequiredOffers: tourBundlePageSize,
  })
  const hasTourBundleOffers = tourBundleOfferList.offerIds.length > 0
  const isTourBundleMode = isTourBundleModeRequested && hasTourBundleOffers

  useEffect(() => {
    if (!shouldOpenDateSelectionModal || hasOpenedDateSelectionModalRef.current || isTourBundleMode) {
      return
    }

    hasOpenedDateSelectionModalRef.current = true
    setIsFlightCalendarOpen(true)
  }, [isTourBundleMode, shouldOpenDateSelectionModal])

  // Charter date ranges from flight API (feeds the date picker and calendar pricing).
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms ?? EmptyArray,
    fareClass: fareClassForSearch,
    disabled: (rooms ?? EmptyArray).length === 0 || isTourBundleMode,
    feederOriginAirportCode: departureAirport.code,
  })
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, nights),
    [allDateRangesByMonth, nights],
  )

  const {
    bundleFilters,
    listKey,
    maldivesOfferIds,
    maldivesOfferCount,
    offers,
    isOffersLoading,
    hasMoreStreamingOffers,
    canStreamHotelOffers,
    minRequiredOffers,
    setOfferPage,
    pageSize,
  } = useMaldivesDirectHotelStreamSearch({
    checkInDate,
    checkOutDate,
    rooms: rooms ?? EmptyArray,
    fareClassForSearch,
    holidayTypes: selectedHolidayTypes,
    amenities: selectedAmenities,
    sortBy: searchParams.sortBy as App.OfferListSortByOptions | undefined,
    isTourBundleMode,
    feederOriginAirportCode: departureAirport.code,
    charterNumberOfNights: nights,
  })

  const allowedFareClassesByOfferId = useMemo(() => {
    const map = new Map<string, Array<FlightsClassTypes>>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.allowedFareClasses)
    })
    return map
  }, [offers, rooms])
  const hasFareClassRestrictionsByOfferId = useMemo(() => {
    const map = new Map<string, boolean>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.taggedFareClasses.length > 0)
    })
    return map
  }, [offers, rooms])
  const availableFareClasses = useMemo(() => {
    const union = getUnionAllowedFareClasses({ offers, occupancies: rooms })
    if (union.length > 0) {
      return union
    }
    const policy = getCharterFareClassPolicy({
      campaignTags: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
      occupancies: rooms,
    })
    return policy.allowedFareClasses
  }, [offers, rooms])
  const defaultAvailableFareClass = useMemo(
    () => getDefaultFareClassFromAllowed(availableFareClasses),
    [availableFareClasses],
  )
  const effectiveAvailableFareClasses = useMemo(() => {
    if (availableFareClasses.length > 0) {
      return availableFareClasses
    }
    return ALL_CHARTER_FARE_CLASSES
  }, [availableFareClasses])
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(effectiveAvailableFareClasses)
  const canOpenFareClassModal = useMemo(
    () => allowedFareClassesForModal.length > 1,
    [allowedFareClassesForModal.length],
  )
  const openFareClassModal = useCallback(async() => {
    if (!canOpenFareClassModal) {
      return
    }
    const hadTripDatesWhenOpening = Boolean(checkInDate && checkOutDate)
    const selectedClass = await openModal<FareClassWithReward>(<MaldivesCharterFlightFareClassModal
      selectedClassId={fareClass ?? FlightsClassTypes.ECONOMY}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={rooms}
      nights={nights}
      luxPlusEnabled={luxPlusEnabled}
      allowedFareClasses={allowedFareClassesForModal}
      rewardSeatsOnly={rewardSeatsOnly}
      onRewardSeatsOnlyChange={setRewardSeatsOnly}
    />)
    if (selectedClass) {
      setFareClassParam(selectedClass)
      setCabinFilter(selectedClass)
      if (isRewardFareClass(selectedClass)) {
        setRewardSeatsOnly(true)
      }
      if (!hadTripDatesWhenOpening) {
        setIsFlightCalendarOpen(true)
      }
    }
  }, [canOpenFareClassModal, openModal, fareClass, checkInDate, checkOutDate, rooms, nights, luxPlusEnabled, allowedFareClassesForModal, setFareClassParam, setCabinFilter, setRewardSeatsOnly, rewardSeatsOnly])
  const mainFlightData = useFlightItemData({
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    rooms,
    fareClass: fareClassForSearch,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    feederOriginAirportCode: departureAirport.code,
  })
  const hasUserSelectedDates = !!(checkInDate && checkOutDate)
  // Only evaluated once dates are picked AND the main flight search has resolved.
  // Stays undefined otherwise so consumers can fall back to the configured-feeder gate.
  const hasFeederFlightPriced = hasUserSelectedDates && !mainFlightData.fetching
    ? hasFeederFlightPricedHelper(mainFlightData.departingFlight, mainFlightData.returningFlight)
    : undefined
  const hasReturnFareFree = hasUserSelectedDates && !mainFlightData.fetching
    ? mainFlightData.hasReturnFareFree
    : undefined
  const oneWayBundle = hasUserSelectedDates && !mainFlightData.fetching &&
    (!!mainFlightData.departingFlight !== !!mainFlightData.returningFlight)
  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: priceMode === 'perPerson',
    fareClass: fareClassForSearch,
    selectedFareClass: fareClass,
    departureAirportCode: departureAirport.code,
    departureAirportName: departureAirport.name,
    charterDepartureDate: departDate,
    duration: nights,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
    hasUserSelectedDates,
    hasFeederFlightPriced,
    hasReturnFareFree,
    oneWayBundle,
    canOpenFareClassModal,
    openFareClassModal,
    selectedCabin,
    rewardSeatsOnly,
    isPromotionPriceApplied: mainFlightData.isPromotionPriceApplied,
  }), [
    priceMode,
    fareClassForSearch,
    fareClass,
    departureAirport.code,
    departureAirport.name,
    departDate,
    nights,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
    hasUserSelectedDates,
    hasFeederFlightPriced,
    hasReturnFareFree,
    oneWayBundle,
    canOpenFareClassModal,
    openFareClassModal,
    selectedCabin,
    rewardSeatsOnly,
    mainFlightData.isPromotionPriceApplied,
  ])
  // Only reset fare class when the user explicitly changes it (not from URL on page load).
  // Track whether the fare class has been changed by user interaction to avoid resetting
  // URL-provided selections when offers load.
  const fareClassUserChangedRef = useRef(false)
  const prevFareClassRef = useRef(fareClass)
  useEffect(() => {
    if (prevFareClassRef.current !== fareClass && prevFareClassRef.current !== undefined) {
      fareClassUserChangedRef.current = true
    }
    prevFareClassRef.current = fareClass
  }, [fareClass])
  useEffect(() => {
    if (!defaultAvailableFareClass || !fareClassUserChangedRef.current) {
      return
    }
    if (fareClass && !availableFareClasses.includes(toBaseFareClass(fareClass))) {
      setFareClassParam(defaultAvailableFareClass)
    }
  }, [availableFareClasses, defaultAvailableFareClass, fareClass, setFareClassParam])

  useEffect(() => {
    if (isTourBundleModeRequested && !tourBundleOfferList.fetching && !hasTourBundleOffers) {
      setOfferMode('hotels')
    }
  }, [isTourBundleModeRequested, tourBundleOfferList.fetching, hasTourBundleOffers, setOfferMode])
  useEffect(() => {
    if (isTourBundleMode && isFlightCalendarOpen) {
      setIsFlightCalendarOpen(false)
    }
  }, [isTourBundleMode, isFlightCalendarOpen])

  // Update Redux suggested dates when check-in/check-out changes so offer URLs use correct dates
  useEffect(() => {
    if (isTourBundleMode || !checkInDate || !checkOutDate || maldivesOfferIds.length === 0) {
      return
    }

    const suggestedDatesKey = buildSuggestedDatesParamsKey('', '', rooms)
    const offerSuggestedDates = maldivesOfferIds.reduce<Record<string, App.OfferSuggestedDates>>((acc, offerId) => {
      acc[offerId] = {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      }
      return acc
    }, {})

    const uniqueSearchTargetId = `maldives-direct-${Places.Maldives.id}`
    const uniqueAvailabilityKey = `maldives-direct-${suggestedDatesKey}`
    const uniqueOfferMetaDataKey = `maldives-direct-${listKey}`

    dispatch({
      type: OFFER_SET_SEARCH_RESULT_METADATA,
      data: {
        distanceFromSearchTarget: {
          [uniqueSearchTargetId]: {},
        },
        offerAvailabilityFromSearchTarget: {
          [uniqueAvailabilityKey]: {},
        },
        offerSuggestedDates: {
          [suggestedDatesKey]: offerSuggestedDates,
        },
        offerMetaData: {
          [uniqueOfferMetaDataKey]: {},
        },
      },
      searchTargetId: uniqueSearchTargetId,
      availabilityKey: uniqueAvailabilityKey,
      suggestedDatesKey,
      offerMetaDataKey: uniqueOfferMetaDataKey,
    })
  }, [dispatch, isTourBundleMode, checkInDate, checkOutDate, maldivesOfferIds, listKey, rooms])

  useEffect(() => {
    if (checkInDate) {
      setSelectedMonth(moment(checkInDate).format(ISO_DATE_SHORT_FORMAT))
    } else {
      const months = Object.keys(dateRangesByMonth)
      if (months.length > 0) {
        setSelectedMonth(months[0])
      }
    }
  }, [checkInDate, dateRangesByMonth])

  const offersHeaderNode = useMemo(() => {
    if (isOffersLoading) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'Gwf6vZLx0VkVILuxpBxT',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter hotel offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '0gwKsC-kP5LNQpR66-Kr',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter hotel offers found',
          },
          { count: maldivesOfferCount },
        )}
      </BodyText>
    )
  }, [isOffersLoading, maldivesOfferCount, intl])
  const tourOffersHeaderNode = useMemo(() => {
    if (tourBundleOfferList.fetching) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'OHpjLwCvgADt7aUGbNJ7',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter tour offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: 'hG3y5JBXM9U8EbMe0yYX',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter tour offers found',
          },
          { count: tourBundleOfferList.offerIds.length },
        )}
      </BodyText>
    )
  }, [tourBundleOfferList.fetching, tourBundleOfferList.offerIds.length, intl])

  // Sync bundle data to Redux store (only when dates are selected)
  useEffect(() => {
    if (isTourBundleMode) {
      return
    }
    if (checkInDate && checkOutDate) {
      updateSelectedDates({
        checkIn: checkInDate,
        checkOut: checkOutDate,
      })
    }

    updateFlightData({
      fetching: mainFlightData.fetching,
      totalPrice: mainFlightData.totalFlightFare,
      journey: mainFlightData.departingFlight,
    })
  }, [
    isTourBundleMode,
    checkInDate,
    checkOutDate,
    mainFlightData,
    updateFlightData,
    updateSelectedDates,
  ])

  const heroHeadingMessage = intl.formatMessage({
    id: '-zpvc9Ylj7B7OUFU95Q3',
    defaultMessage: 'Fly direct from Melbourne to the Maldives',
    description: 'Hero heading for Maldives Direct search page in the search interface',
  })
  const heroEyebrowText = intl.formatMessage({
    id: 'r1jHBacJ1MPgarEJjTZ6',
    defaultMessage: 'Direct flights & hotel packages',
    description: 'Eyebrow label above the hero heading on the Maldives charter landing page, describing the bundle type',
  })
  const heroSubheadingText = intl.formatMessage({
    id: 'riBW9C3_4If_rWph_M8F',
    defaultMessage: "Spend more time in paradise with Australia's only direct flight to Malé.",
    description: 'Supporting copy under the hero heading on the Maldives charter landing page highlighting the direct flight to Malé',
  })
  const hotelAndFlightsText = intl.formatMessage({
    id: 'LQz54ECWwYqY5AspWsJ7',
    defaultMessage: 'Hotel & flights',
    description: 'Hero banner button on the Maldives charter landing page that shows the hotel and flight bundle offers',
  })
  const tourAndFlightsText = intl.formatMessage({
    id: 'CmtKZsDdzDSoTnAF_E4U',
    defaultMessage: 'Tour & flights',
    description: 'Hero banner button on the Maldives charter landing page that shows the tour and flight bundle offers',
  })
  const contactBannerText = intl.formatMessage({
    id: 'LBwcJkNj6L-_WNjHEcSh',
    defaultMessage: 'Our Maldives travel experts can help with any questions',
    description: 'Contact banner text for Maldives charter support in the maldives direct search page section',
  })
  const hundredDollarSaleTermsText = intl.formatMessage(
    {
      id: 'jMKG-GVENHsF57Cct0f0',
      defaultMessage: 'Promoter: Luxury Escapes Travel Pty Ltd (ABN 16 150 650 927). 100 economy class return flight tickets, MEL to MLE, {fareAmount} per ticket, available only as part of a Luxury Escapes Maldives bundle 7:00pm AEST on Monday 18 May 2026 to 11:59pm AEST on Monday 25 May 2026, or until the 100-ticket allocation sells out. Open to Australian residents. First come, first served. Limited seats. Additional costs apply per standard package inclusions and exclusions. Flight ticket is non-refundable. Not combinable with any other Luxury Escapes promotion, discount, credit, or offer. Bookings are only available from the commencement of the Offer Period; no bookings or reservations will be accepted prior to 7:00pm AEST on Monday 18 May 2026.',
      description: 'Mini terms and conditions for the Maldives charter hundred dollar sale',
    },
    { fareAmount: hundredDollarSaleFareAmount },
  )
  const hundredDollarSaleTermsTitle = intl.formatMessage(
    {
      id: 'E45Q-VjuLfEatoyIC4_y',
      defaultMessage: 'T&Cs (100 seats at {fareAmount} Sale)',
      description: 'Title for the Maldives charter hundred dollar sale mini terms and conditions',
    },
    { fareAmount: hundredDollarSaleFareAmount },
  )
  const disclaimerText = intl.formatMessage({
    id: 'a3_bUxyxhmM7uSAUglP7',
    defaultMessage: '*Based on average time savings compared with non-direct flights from Melbourne to Malé operated by competitor airlines. Calculation includes estimated total door-to-door travel time, encompassing flight duration, transfers, layovers and hotel check-in/check-out times.',
    description: 'Legal disclaimer about charter flight transit time savings comparison',
  })
  const selectTripDatesText = intl.formatMessage({
    id: 'oU0vVHgO-s0CqiSIjI2l',
    defaultMessage: 'Select your trip dates',
    description: 'Heading title for Maldives charter flight calendar modal',
  })

  if (!maldivesCharterEnabled) {
    // show 404 error page
    return <Error404PageWithOffers />
  }

  return (
    <div>
      <div ref={heroDetectorRef}>
        <HeroBannerSection>
          <HeroBanner>
            <HeroPanel>
              <Group direction="vertical" gap={16} tabletGap={24} fullWidth>
                <Group direction="vertical" gap={4} tabletGap={8}>
                  {canShowFamilyAndFriendsDiscountChip && <AssistChip
                    variant="filled"
                    size="medium"
                    startIcon={<LineSmileIcon />}
                    endIcon={<LineAngleRightIcon />}
                    onClick={handleFamilyAndFriendsDiscountClick}
                  >
                    {familyAndFriendsDiscountLabel}
                  </AssistChip>}
                  <BodyTextBlock variant="large" weight="bold" colour="highlight-primary" align="center" tabletAlign="start">
                    {heroEyebrowText}
                  </BodyTextBlock>
                  <Heading variant="heading1" as="h1" colour="highlight-primary-dark-contrast" align="center" tabletAlign="start">
                    {heroHeadingMessage}
                  </Heading>
                  <BodyTextBlock variant="large" colour="highlight-primary-dark-contrast" align="center" tabletAlign="start">
                    {heroSubheadingText}
                  </BodyTextBlock>
                </Group>

                {hasTourBundleOffers && !isAgentHubEnabled && <HeroCtaButtons direction="horizontal" gap={12} fullWidth>
                  <TextButton
                    kind="primary"
                    variant="dark"
                    shape="round"
                    size="medium"
                    onClick={handleHotelsModeClick}
                  >
                    {hotelAndFlightsText}
                  </TextButton>
                  <TextButton
                    kind="primary"
                    variant="dark"
                    shape="round"
                    size="medium"
                    onClick={handleTourBundlesModeClick}
                  >
                    {tourAndFlightsText}
                  </TextButton>
                </HeroCtaButtons>}
              </Group>
            </HeroPanel>

            <HeroBannerMedia>
              <HeroBannerImage
                id={MALDIVES_LANDING_BANNER_IMAGE_ID}
                fit="center"
                objectFit="cover"
                alt=""
              />
              <HeroBannerArc aria-hidden />
            </HeroBannerMedia>
          </HeroBanner>

          <ContactPromptRow
            direction="vertical"
            tabletDirection="horizontal"
            gap={12}
            horizontalAlign="center"
            verticalAlign="center"
          >
            <BodyText variant="large" align="center">
              {contactBannerText}
            </BodyText>
            {!isAgentHubEnabled && <TextLink
              size="large"
              weight="bold"
              underline={false}
              startIcon={<LinePhoneIcon />}
              href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
              rel="nofollow"
            >
              {MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY}
            </TextLink>}
          </ContactPromptRow>

        </HeroBannerSection>
      </div>
      {onePointPromo.enabled && (
        <PromoBannerContainer>
          <MaldivesOnePointPromoBanner onLearnMore={onePointPromo.onLearnMore} onViewOffers={onePointPromo.onViewOffers} />
        </PromoBannerContainer>
      )}
      {hasTourBundleOffers && !isAgentHubEnabled && (
        <CSSBreakpoint min="tablet">
          <LayoutContainer relative>
            <OfferModeToggleWrapper id="maldives-direct-mode-toggle-desktop" className={!heroInView ? 'full-size' : undefined}>
              <ModeToggleTabs
                variant="desktop"
                isTourBundleMode={isTourBundleMode}
                onHotelsModeClick={handleHotelsModeClick}
                onTourBundlesModeClick={handleTourBundlesModeClick}
                showFullSize={!heroInView}
                modeToggleScale={modeToggleScale}
                modeToggleRef={modeToggleRef}
              />
            </OfferModeToggleWrapper>
          </LayoutContainer>
          <ModeToggleBottomSpacer />
        </CSSBreakpoint>
      )}

      {hasTourBundleOffers && !isAgentHubEnabled && (
        <MobileOfferModeToggleStickyWrapper max="mobile">
          <OfferModeToggleWrapper id="maldives-direct-mode-toggle-mobile">
            <ModeToggleTabs
              variant="mobile"
              isTourBundleMode={isTourBundleMode}
              onHotelsModeClick={handleHotelsModeClick}
              onTourBundlesModeClick={handleTourBundlesModeClick}
            />
          </OfferModeToggleWrapper>
        </MobileOfferModeToggleStickyWrapper>
      )}

      {/* Bundle Search Section */}
      {!isTourBundleMode && <MaldivesCharterFilters
        searchState={{
          rooms,
          selectedBundle,
          departureAirport: departureAirport,
          flightClass: fareClass,
          luxPlusEnabled,
          checkInDate,
          checkOutDate,
          departDate,
          returnDate,
        }}
        onRoomsChange={setRooms}
        onRoomsApply={checkAndResetFareClassIfNeeded}
        onBundleChange={handleBundleChange}
        onDateSelect={handleDateSelect}
        onAirportChange={handleAirportChange}
        onFlightClassChange={handleFlightClassChange}
        onOpenFlightCalendar={handleOpenFlightCalendar}
        availableFareClasses={availableFareClasses}
        rewardSeatsOnly={rewardSeatsOnly}
        onRewardSeatsOnlyChange={setRewardSeatsOnly}
        onCabinFilterChange={setCabinFilter}
      />}

      {/* Offers Section - using same data flow as HotelSearchPage */}
      <OffersSection id={MALDIVES_DIRECT_OFFERS_SECTION_ID}>
        <OffersContainer>
          <VerticalSpacer gap={32}>
            {isTourBundleMode && (
              <Heading id={MALDIVES_DIRECT_SEARCH_FILTERS_HEADING_ID} variant="heading2" as="h2" align="center">
                {tourModeBundlesHeading}
              </Heading>
            )}
            {!isTourBundleMode && <ReturnCharterFlightTile
              route={MALDIVES_CHARTER_ROUTE}
              fareClass={fareClassForSearch}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              occupancies={rooms}
              flightOrigin={departureAirport.code === MELBOURNE_AIRPORT.code ? undefined : departureAirport}
              onViewCharterDates={handleOpenFlightCalendar}
            />}
            {!isTourBundleMode && <MobileFilterTabsPadding>
              <AnywhereFilterTabs
                filters={bundleFilters ?? {}}
                search={windowSearch}
              />
            </MobileFilterTabsPadding>}
            <OffersHeader direction="vertical" gap={8} tabletDirection="horizontal" tabletVerticalAlign="center" tabletHorizontalAlign="space-between">
              {isTourBundleMode ? tourOffersHeaderNode : offersHeaderNode}
              {!isTourBundleMode && <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between" wrap="nowrap">
                <OfferSortControl
                  sortOptions={maldivesSortOptions}
                  onSortChange={handleSortChange}
                />
                <HotelPriceToggle value={priceMode} onChange={handlePriceModeChange} />
              </Group>}
            </OffersHeader>
          </VerticalSpacer>
          {isTourBundleMode && (
            <OfferList
              filters={tourBundleFilters}
              tileStyle="search"
              pageSize={tourBundlePageSize}
              showLoadingTiles={tourBundleOfferList.fetching || tourBundleOfferList.offerIds.length === 0}
              disableUseOfferList
              extraOfferListAndAction={{ action: OfferListAction.OVERRIDE, offerList: tourBundleOfferList }}
              overrideFetching={tourBundleOfferList.fetching}
              minRequiredOffers={tourBundlePageSize}
            />
          )}
          {!isTourBundleMode && (
            <MaldivesDirectBundleProvider value={bundleContextValue}>
              <OfferList
                filters={bundleFilters ?? {}}
                tileStyle="search"
                pageSize={pageSize}
                onPageChange={setOfferPage}
                minRequiredOffers={minRequiredOffers}
                disableUseOfferList={!canStreamHotelOffers}
              />
            </MaldivesDirectBundleProvider>
          )}
        </OffersContainer>
      </OffersSection>

      {!hasMoreStreamingOffers && <DreamBlock
        tiles={dreamTiles}
        title={intl.formatMessage({
          id: 'V-qmg9wDsPt0QYJmClE6',
          defaultMessage: 'Dream about the Maldives',
          description: 'Title for the dream block section on Maldives Direct search page',
        })}
      />}

      {/* Contact Information Banner */}
      <ContactBannerContainer>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" verticalAlign="center" gap={16}>
            <BodyText variant="large">
              {contactBannerText}
            </BodyText>
            <MaldivesSupportPhoneNumber />
          </Group>
          {isCharterLaunchOffer100Enabled && (
            <Group direction="vertical" gap={4}>
              <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                {hundredDollarSaleTermsTitle}
              </BodyText>
              <BodyText variant="small" colour="light-foreground-alt">
                {hundredDollarSaleTermsText}
              </BodyText>
              <BodyText variant="small" colour="light-foreground-alt">
                <FormattedMessage
                  id="RC6pZSPR_AjCgmWZRMp0"
                  defaultMessage="Full T&Cs: <link>{fullTermsUrl}</link>"
                  description="Full terms and conditions link for the Maldives charter hundred dollar sale"
                  values={{
                    fullTermsUrl: HUNDRED_DOLLAR_SALE_FULL_TERMS_URL,
                    link: (chunks) => (
                      <TextLink href={HUNDRED_DOLLAR_SALE_FULL_TERMS_URL} target="_blank" rel="noopener noreferrer" weight="regular">
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </Group>
          )}
          <BodyText variant="small" colour="light-foreground-alt">
            {disclaimerText}
          </BodyText>
        </Group>
      </ContactBannerContainer>

      <MaldivesDirectSearchCalendarModal
        isOpen={isFlightCalendarOpen}
        title={selectTripDatesText}
        onClose={handleCloseFlightCalendar}
        onDateSelect={handleFlightDateSelect}
        nights={nights}
        rooms={rooms}
        luxPlusEnabled={luxPlusEnabled}
        dateRangesByMonth={dateRangesByMonth}
        selectedMonth={selectedMonth}
        onMonthChange={handleMonthChange}
        fareClassForSearch={fareClassForSearch}
        selectedFareClass={fareClass ?? null}
        priceMode={priceMode}
        onPriceModeChange={handlePriceModeChange}
        selectedOptionId={`${checkInDate}-${nights}n`}
        canStreamHotelOffers={canStreamHotelOffers}
        onDurationChange={handleDurationChangeFromCalendar}
        durationOptions={charterBundleDurationOptions}
        onLuxPlusToggle={handleLuxPlusToggle}
        onFareClassChange={handleFlightClassChange}
        availableFareClasses={allowedFareClassesForModal}
        rewardSeatsOnly={rewardSeatsOnly}
        cabinFilter={selectedCabin}
        onCabinFilterChange={setCabinFilter}
        onRewardSeatsOnlyChange={setRewardSeatsOnly}
        infiniteMemberOnly={infiniteMemberOnlyForTier}
        onInfiniteMemberOnlyChange={setInfiniteMemberOnly}
        feederOriginAirportCode={departureAirport.code}
      />

      {/* Floating Call Prompt - appears when hero is out of view */}
      <MaldivesCallPrompt
        show={!heroInView && !isCallPromptClosed && !agentHubOrSubBrandConfig.isEnabled}
        onClose={() => setIsCallPromptClosed(true)}
      />
    </div>
  )
}

export default MaldivesDirectSearchPage
